import React, { useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Grid,
  FormControlLabel,
  Paper,
  Table,
  TableContainer,
  TableCell,
  TableRow,
  TablePagination,
  TableHead,
  TableBody,
} from '@material-ui/core';

import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { makeStyles } from '@material-ui/styles';

import { useAPI } from '../../../utils/api';
import { useAuth } from '../../../utils/auth';
import { useHistory } from 'react-router-dom';
import { MODE } from '../../../utils/mode';
import { useDialog } from '../../../utils/dialog';

const useStyles = makeStyles({
  root: {
    '&:last-of-type td': {
      borderBottom: 'none',
    },
  },
});

const generateDto = ({ id, code, description }) => {
  return {
    id,
    code,
    description,
  };
};

const columns = [
  {
    id: 'code',
    label: '特殊分帳代碼',
  },
  {
    id: 'description',
    label: '特殊分帳說明',
  },
  {
    id: 'function',
    label: '功能',
    minWidth: 440,
  },
];

const SpecialSubAccountListPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const dialog = useDialog();
  const auth = useAuth();
  const api = useAPI();
  const specialSubAccountApi = api.specialSubAccountApi;

  const memberID = auth.user.memberID;

  const [isUnmounted, setIsUnmounted] = useState(false);
  const [dataRows, setDataRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [shouldPageUpdate, setShouldPageUpdated] = useState(false);
  const [flagRequired, setFlagRequired] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPagedListByApi(newPage + 1, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = +event.target.value;
    setPageSize(newPageSize);
    setPage(0);
    fetchPagedListByApi(1, newPageSize);
  };

  const fetchPagedListByApi = (pg, pgSize) => {
    specialSubAccountApi
      .fetchList({
        // differ by 1 from api params
        page: pg,
        pageSize: pgSize,
      })
      .then((info) => {
        setRowCount(info.rowCount);

        const rows = (info.rowList || []).map((data) => {
          return generateDto(data);
        });
        setDataRows(rows);
      })
      .catch((error) => {
        dialog.openAlert({ message: error.message });
      });
  };

  const deleteData = (dataId) => {
    specialSubAccountApi
      .deleteData(dataId)
      .then((isOK) => {
        if (isOK) {
          fetchPagedListByApi(page, pageSize);
        }
      })
      .catch((error) => {
        dialog.openAlert({ message: error.message });
      });
  };

  const fetchSubAccountSetting = () => {
    specialSubAccountApi.fetchRequiredFlag().then((data) => {
      setFlagRequired(data.is_special_sub_account_required);
    });
  };

  const updateSubAccountSetting = (flag) => {
    specialSubAccountApi
      .updateRequiredFlag(flag)
      .then((isOK) => {
        if (isOK) {
          setFlagRequired(flag);
        } else {
          dialog.openAlert({ message: '設定失敗' });
        }
      })
      .catch((error) => {
        dialog.openAlert({ message: error.message });
      });
  };

  const gotoEditMode = (id) => {
    history.push(`/member/special-sub-account/${MODE.EDIT}/${id || ''}`);
  };
  const gotoViewMode = (id) => {
    history.push(`/member/special-sub-account/${MODE.VIEW}/${id || ''}`);
  };

  const gotoNewMode = () => {
    history.push(`/member/special-sub-account/${MODE.NEW}`);
  };

  useEffect(() => {
    if (isUnmounted) return;

    if (!memberID || memberID === 'undefined' || memberID === 'null') {
      history.replace('/');
    }

    fetchSubAccountSetting();
    fetchPagedListByApi(page, pageSize);

    return () => {
      setIsUnmounted(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dataRows) return;

    setShouldPageUpdated(true);
  }, [dataRows]);

  useEffect(() => {
    setShouldPageUpdated(false);
  }, [shouldPageUpdate]);

  return (
    <>
      <Grid container spacing={3} sx={{ width: '80%', marginBottom: 2 }}>
        <Grid item xs={12} sm={3} md={3} sx={{ maxWidth: '120px' }}>
          <Button
            type="button"
            fullWidth
            variant="contained"
            color="success"
            sx={{ color: 'white' }}
            startIcon={<PersonAddIcon />}
            onClick={() => gotoNewMode()}
          >
            新增特殊分帳
          </Button>
        </Grid>
        <Grid item xs={12} sm={9} md={9}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={flagRequired}
                onClick={() => {
                  const flag = !flagRequired;
                  updateSubAccountSetting(flag);
                }}
              />
            }
            label="訂單之特殊分帳欄位必填"
          />
        </Grid>
      </Grid>

      {!shouldPageUpdate ? (
        <Paper
          sx={{ width: '100%', overflow: 'hidden', padding: 1 }}
          elevation={5}
        >
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {dataRows
                    ? columns.map((column, index) => (
                        <TableCell
                          key={`${column.id}-${index}`}
                          align={column.align || 'center'}
                          sx={{
                            minWidth: column.minWidth || 'none',
                            padding: 1,
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))
                    : undefined}
                </TableRow>
              </TableHead>

              <TableBody>
                {dataRows
                  ? dataRows.map((row, index) => (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={`${row.id}-${index}`}
                        className={classes.root}
                      >
                        {columns.map((column, index) => (
                          <TableCell
                            key={`${column.id}-${index}`}
                            align={column.align || 'center'}
                            sx={{
                              padding: 1,
                              minHeight: '0',
                              maxWidth: '100%',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {column.id !== 'function' ? (
                              row[column.id]
                            ) : (
                              <Grid container columns={36} spacing={1}>
                                <Grid item xs={8}>
                                  <Button
                                    type="button"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    startIcon={<EditIcon />}
                                    onClick={() => gotoEditMode(row.id)}
                                    sx={{ maxWidth: '180px' }}
                                    size="small"
                                  >
                                    編輯
                                  </Button>
                                </Grid>
                                <Grid item xs={8}>
                                  <Button
                                    type="button"
                                    fullWidth
                                    variant="contained"
                                    color="info"
                                    startIcon={<RemoveRedEyeIcon />}
                                    onClick={() => gotoViewMode(row.id)}
                                    sx={{
                                      maxWidth: '180px',
                                      color: 'white',
                                    }}
                                    size="small"
                                  >
                                    更多
                                  </Button>
                                </Grid>
                                <Grid item xs={8}>
                                  <Button
                                    type="button"
                                    fullWidth
                                    variant="contained"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() =>
                                      dialog.openConfirm({
                                        message: `確定要刪除 "${row.code}" 此筆資料嗎？`,
                                        cancelColor: 'gray',
                                        confirmColor: 'warning',
                                        confirmText: '刪除',
                                        onConfirm: () => {
                                          setTimeout(() => {
                                            deleteData(row.id);
                                          }, 100);
                                        },
                                      })
                                    }
                                    sx={{ maxWidth: '180px' }}
                                    size="small"
                                  >
                                    刪除
                                  </Button>
                                </Grid>
                              </Grid>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : undefined}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            labelRowsPerPage="每頁顯示"
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rowCount}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : undefined}
    </>
  );
};

export default SpecialSubAccountListPage;
